/* A1 — Sign in.
 *
 * *** THIS SCREEN MATCHES THE PUBLIC SITE, THE REST OF THE PORTAL DOES NOT ***
 * An admin arrives here from mentorsconnect.memofaceai.com, so the handoff
 * should not look like a different product. But the portal behind the login is
 * deliberately left on its existing indigo — the user asked for the login page
 * and the legal pages to be in sync with the site, and for the portal UI to
 * stay as it is.
 *
 * *** HOW THAT IS DONE WITHOUT TOUCHING ANY OTHER SCREEN ***
 * `Button` and `TextField` are shared components that read `--indigo` from
 * tokens.css. Redefining those custom properties HERE, on .page, recolours
 * those components on this screen only — the cascade stops at this subtree.
 * Editing tokens.css instead would repaint all 11 portal areas.
 */

.page {
  /* The site's palette, scoped. These names shadow the globals from
     tokens.css for everything rendered inside .page. */
  --indigo: #1b4d3e;
  --indigo-dark: #123529;
  --indigo-soft: #e8f1ed;

  /* *** THE LIGHT SURFACE SET, PINNED — THIS IS NOT OPTIONAL ***
     The shared TextField reads --bg-elevated and --text. tokens.css flips both
     under `@media (prefers-color-scheme: dark)`, so on a machine set to dark
     mode the inputs rendered as near-black blocks inside a card I had forced
     to white, with labels almost invisible against it. Reported 2026-09-18.

     An earlier attempt overrode only `:root[data-theme='dark']` and missed the
     media query entirely — which is the path most people actually hit, since
     it needs no in-app toggle.

     Redefining the surface tokens here fixes every shared component at once,
     rather than patching each one: the public site is light-only, so a dark
     sign-in page would defeat the continuity this screen exists to create. */
  --bg: #fcfcfa;
  --bg-elevated: #ffffff;
  --bg-sunken: #f5f4f0;
  --bg-hover: #f5f4f0;
  --text: #14140f;
  --text-muted: #67645c;
  --text-subtle: #94918a;
  --border: #e4e2db;
  --border-strong: #d2cfc6;

  /* *** THE SEMANTIC TOKENS FLIP IN DARK MODE TOO ***
     Pinning only the neutrals was a half fix: `--danger-soft` becomes the dark
     maroon #3d1618, so the sign-in error rendered as dark red text on a dark
     red block — barely readable, on the one message an admin most needs to
     read. Reported 2026-09-18.

     All four pairs are pinned, not just danger: a future message on this
     screen would hit exactly the same wall. */
  --success: #0e9f6e;
  --success-soft: #e3f5ee;
  --warning: #d97706;
  --warning-soft: #fef3e2;
  --danger: #b42318;
  --danger-soft: #fef3f2;
  --info: #2563eb;
  --info-soft: #e8f0fe;

  --site-green: #1b4d3e;
  --site-green-mid: #2e7a61;
  --site-blue: #23486b;
  --site-blue-mid: #36699c;
  --site-paper: #fcfcfa;
  --site-ink: #14140f;
  --site-ink-muted: #67645c;
  --site-rule: #e4e2db;

  /*
    *** 100% OF #root, NOT 100vh ***
    `#root` already carries `min-height: 100vh` from global.css. Stating
    100vh again here put a viewport-tall box INSIDE a viewport-tall box, so
    a page holding nothing but a login card scrolled — reported 2026-10-31.

    `100%` of a `min-height` parent resolves to auto, which is exactly what
    is wanted: the grid fills what it is given and grows only if the card
    genuinely needs more room. `min-height: 100dvh` is the fallback for
    when this subtree is ever rendered outside #root — dvh, not vh, so a
    mobile browser's collapsing toolbar does not create the same overflow.
  */
  /* No height of its own: #root is a single-row grid, so this stretches to
     fill it. Restating 100vh here is what made the page scroll. */
  display: grid;
  place-items: center;
  padding: var(--space-5);

  /* The site's warm off-white, not the portal's cool grey — this is the first
     thing that reads as "same product" before any text is parsed. */
  background:
    radial-gradient(circle at 12% 8%, rgb(54 105 156 / 10%), transparent 42%),
    radial-gradient(circle at 88% 88%, rgb(46 122 97 / 10%), transparent 42%),
    var(--site-paper);
}

/* No separate dark-theme rule is needed: the surface tokens pinned on .page
   above already hold for every theme path — the in-app [data-theme] toggle AND
   the prefers-color-scheme media query. A second rule here was what created
   the illusion the dark case was handled while the media query went straight
   past it. */

.card {
  width: 100%;
  max-width: 420px;
  /* Generous and asymmetric — more above and below than at the sides, which is
     what stops a form card reading as a cramped box. `--space-7` does not
     exist in tokens.css, so these are explicit rather than a silent fallback. */
  padding: 2.75rem 2.25rem 2.25rem;
  background: var(--bg-elevated);
  border: 1px solid var(--site-rule);
  /* Softer, wider corners than the portal's, matching the site's cards. */
  border-radius: 22px;
  /* Layered and very soft. A single heavy blur is what makes a card look
     cheap; the site uses the same two-part shadow. */
  box-shadow:
    0 1px 2px rgb(20 20 15 / 4%),
    0 24px 60px -32px rgb(20 20 15 / 18%);
}

/* The mark and the name, centred: a sign-in card has one axis and fighting it
   with a left-aligned header is what made the original look like a fragment of
   a larger page. */
.brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  margin-bottom: 2rem;
  padding-bottom: 1.75rem;
  border-bottom: 1px solid var(--site-rule);
}

/* *** THE REAL MARK, NOT A TEXT MONOGRAM ***
   This was the letters "MC" in an indigo square. It is now the site's actual
   logo — two overlapping rings whose shared area is the session — drawn inline
   so it scales and needs no asset request. */
.brandMark {
  /* Larger than a header mark: here it is the only graphic on the screen and
     carries the brand on its own. */
  width: 54px;
  height: 54px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
}

.brandMark svg {
  width: 100%;
  height: 100%;
}

.title {
  /* The site's display face. Falls back through the same stack, so a failed
     webfont still lands on a serif rather than reverting to the portal's
     grotesque. */
  font-family: 'Fraunces', 'Iowan Old Style', Georgia, 'Times New Roman', serif;
  font-size: 1.55rem;
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1.15;
  color: var(--site-ink);
}

.subtitle {
  margin-top: 3px;
  font-size: var(--text-sm);
  color: var(--site-ink-muted);
}

/* The form needs air between the header and the first field, and the fields
   need more room between them than the portal's dense tables use. */
/* The fields need more room between them than the portal's dense tables use. */
.form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* *** AN AUTOFILLED FIELD MUST NOT LOOK DISABLED ***
   Chrome and Edge paint their own background over an autofilled input —
   a flat blue-grey that reads as greyed-out. On this screen the email is
   almost always autofilled and the password is not, so the two fields looked
   like one was locked. Reported 2026-09-18.

   *** WHY `box-shadow` AND NOT `background` ***
   The autofill background cannot be overridden by `background` at all: the
   browser paints it in a layer the cascade does not reach. A large inset
   box-shadow is the one reliable way to cover it, and `-webkit-text-fill-color`
   is likewise the only way to set the text colour.

   The long transition delay is a second, unrelated trick: Chrome re-applies
   its background on some interactions, and a delay measured in hours means
   the override never visibly loses. */
.form input:-webkit-autofill,
.form input:-webkit-autofill:hover,
.form input:-webkit-autofill:focus,
.form input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 100px var(--bg-elevated) inset;
  box-shadow: 0 0 0 100px var(--bg-elevated) inset;
  -webkit-text-fill-color: var(--text);
  caret-color: var(--text);
  transition: background-color 9999s ease-in-out 0s;
}

/* Firefox uses its own mechanism and does respond to a normal background. */
.form input:autofill {
  background: var(--bg-elevated);
  color: var(--text);
}

/* *** THE FOCUS GLOW IS HARDCODED INDIGO IN Field.module.css ***
   `box-shadow: 0 0 0 3px rgb(79 63 240 / 15%)` is a literal, not a token, so
   redefining --indigo on this page cannot reach it — focusing a field would
   flash the portal's indigo on a green page. Restated here in green.

   The autofill rule above must not be broken by this, so the focused +
   autofilled case carries BOTH shadows. */
.form input:focus {
  border-color: var(--site-green);
  box-shadow: 0 0 0 3px rgb(27 77 62 / 14%);
}

.form input:-webkit-autofill:focus {
  -webkit-box-shadow:
    0 0 0 100px var(--bg-elevated) inset,
    0 0 0 3px rgb(27 77 62 / 14%);
  box-shadow:
    0 0 0 100px var(--bg-elevated) inset,
    0 0 0 3px rgb(27 77 62 / 14%);
}

/* *** A WRONG PASSWORD IS NOT AN EMERGENCY ***
   A saturated block with a full red border shouts at someone who has simply
   mistyped. The weight moves to a single accent bar on the leading edge, with
   readable dark-red text on a pale tint — noticeable without alarming.

   The bar is on the inline-start edge so it follows the text direction rather
   than being pinned to the left. */
.error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  font-size: var(--text-sm);
  line-height: 1.5;
  color: #7a271a;
  background: var(--danger-soft);
  border: 1px solid #fecdca;
  border-inline-start: 3px solid var(--danger);
  border-radius: 10px;
}

/* The icon is decorative — the text already carries the meaning, and the
   element is role="alert" so a screen reader announces it. */
.error svg {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--danger);
}

.footnote {
  margin-top: var(--space-5);
  padding-top: var(--space-4);
  border-top: 1px solid var(--site-rule);
  font-size: var(--text-xs);
  color: var(--site-ink-muted);
  text-align: center;
}

/* A quiet line back to the public site. An admin who lands here by mistake, or
   who wants the platform's own pages, currently has no way out except the
   browser's back button. */
.back {
  margin-top: var(--space-4);
  text-align: center;
  font-size: var(--text-xs);
}

.back a {
  color: var(--site-ink-muted);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.back a:hover {
  color: var(--site-green);
}

/* ---------------------------------------------------- role tabs (new) */

/*
  Three audiences, one page. A segmented control rather than links: the
  choice is a mode of this form, not a different destination, and a student
  who picks wrong should recover in one click.
*/
.tabs {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 4px;
  padding: 4px;
  margin-bottom: 20px;
  background: var(--bg-sunken);
  border-radius: var(--radius);
}

.tab {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 9px 12px;
  border-radius: calc(var(--radius) - 3px);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-muted);
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}

.tab:hover {
  color: var(--text);
}

.tabActive {
  background: var(--bg-elevated);
  color: var(--text);
  box-shadow: 0 1px 2px rgb(0 0 0 / 8%);
}

.tab:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 1px;
}

/*
  "If that number is registered, a code has been sent."

  *** INFORMATION, NOT SUCCESS ***
  Styled neutrally on purpose: a green tick would read as "yes, that account
  exists", which is exactly what the server refuses to confirm.
*/
.notice {
  margin: 0;
  padding: 10px 12px;
  background: var(--bg-sunken);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  color: var(--text-muted);
  line-height: 1.45;
}

/* "Use a different number" — an escape hatch, not a primary action. */
.linkButton {
  appearance: none;
  border: 0;
  background: none;
  padding: 0;
  align-self: flex-start;
  font: inherit;
  font-size: var(--text-sm);
  color: var(--indigo);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.linkButton:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

/*
  An institute's own uploaded logo, where our SVG mark would otherwise sit.

  `contain`, not `cover`: a client's logo is their identity and must not be
  cropped to fill a square. Whatever aspect ratio they uploaded is shown
  whole, centred in the same 54px slot the platform mark occupies, so the
  card's layout does not shift between the two.
*/
.brandLogo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  /* A transparent PNG on a white card is common and fine; a logo with its
     own white box should not gain a visible edge, so no background here. */
}
